import { useEffect, useMemo, useState } from 'react';
import { orderApi } from '@formosa/shared/api/order';
import {
  formatThumbChange,
  formatThumbPrice,
  normalizeSymbolCode,
} from '@formosa/shared/utils/marketThumb';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';

export const PRODUCT_LIST_TABS = [
  { key: 'timed', label: '限時', playType: 'TIME_LIMITED' },
  { key: 'delivery', label: '交割', playType: 'FUTURES' },
  { key: 'perpetual', label: '永續', playType: 'PERPETUAL' },
];

export default function ProductListPanel({
  title = '全部產品',
  showClose = false,
  onClose,
  onProductClick,
  initialTab = 'timed',
  className = '',
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const activePlayType = useMemo(
    () => PRODUCT_LIST_TABS.find((tab) => tab.key === activeTab)?.playType || 'TIME_LIMITED',
    [activeTab]
  );

  const symbolCodes = useMemo(
    () => products.map((product) => product.marketSymbolCode).filter(Boolean),
    [products]
  );
  const thumbs = useMarketThumbSocket(symbolCodes);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const list = await orderApi.listParentProducts(activePlayType);
        const rows = Array.isArray(list) ? list : [];
        if (!cancelled) {
          setProducts(rows);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [activePlayType]);

  return (
    <div className={`products-page ${className}`.trim()}>
      <header className={`products-page__header ${showClose ? 'has-close' : ''}`}>
        <h1>{title}</h1>
        {showClose && (
          <button type="button" className="products-page__close" aria-label="關閉" onClick={onClose}>
            ×
          </button>
        )}
      </header>

      <div className="products-page__tabs">
        {PRODUCT_LIST_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`products-page__tab ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <p className="products-page__status">載入中…</p>}

      {!loading && (
        <ul className="products-page__list">
          {products.map((product) => {
            const code = normalizeSymbolCode(product.marketSymbolCode);
            const thumb = thumbs[code];
            const change = formatThumbChange(thumb);
            const symbolCode = product.displayName || product.marketSymbolCode;
            const pairLabel = product.name || product.marketSymbolCode;
            return (
              <li key={product.id}>
                <button
                  type="button"
                  className="products-page__item"
                  onClick={() => onProductClick?.(product, activePlayType)}
                >
                  <div className="products-page__left">
                    <span className="products-page__icon">{symbolCode?.slice(0, 1)}</span>
                    <div>
                      <div className="products-page__symbol">{symbolCode}</div>
                      <div className="products-page__pair">{pairLabel}</div>
                    </div>
                  </div>
                  <div className="products-page__right">
                    <div className="products-page__price">{formatThumbPrice(thumb?.price, 4)}</div>
                    <div className={`products-page__change ${change.positive ? 'is-up' : 'is-down'}`}>
                      {change.text}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
          {products.length === 0 && (
            <li className="products-page__empty">無資料</li>
          )}
        </ul>
      )}
    </div>
  );
}
